import React from 'react';
import { Button, FieldGroup } from '@ver-uds/react';
import classNames from 'classnames';
import { Alert } from '@ver-uds/uswds-react';
import Section from '../../Section';
import EVerifyUser from '../EVerifyUsers/data/EVerifyUser';
import PointOfContact from './PointOfContact';
import { useToggles } from '../../../context/Toggles/TogglesContext';

export type CommonEVerifyUsersReviewProps = {
  users?: EVerifyUser[];
  onEditButtonClick: () => void;
};

export type AdminField = {
  fieldText: string;
  admins?: EVerifyUser[];
};

export type MOUUser = {
  firstName: string;
  lastName: string;
  emailAddress: string;
};

export type MouSignatoryField = {
  mouSignatories?: MOUUser[];
};

export interface BaseEVerifyUsersReviewProps {
  sectionText?: string;
  editButtonText?: string;
  showPointOfContact?: boolean;
  adminField?: AdminField;
  mouSignatoryField?: MouSignatoryField;
  onEditButtonClick: () => void;
  rightAlignOnEditButton?: boolean;
  flipOrder?: boolean;
  displayCorporateAdminMFAAlert?: boolean;
}

const BaseEVerifyUsersReview = ({
  sectionText = 'E-Verify Users',
  editButtonText = 'Edit Users',
  showPointOfContact = false,
  adminField,
  mouSignatoryField,
  onEditButtonClick,
  rightAlignOnEditButton = false,
  flipOrder = false,
  displayCorporateAdminMFAAlert = false,
}: BaseEVerifyUsersReviewProps): JSX.Element => {
  const toggles = useToggles();
  const isMfaEnvironment = toggles?.get('MFA_USER_CREATION') === 'on';

  const displayMOUSignatory = (): JSX.Element => {
    if (!mouSignatoryField) {
      return <></>;
    }

    const mouSignatoriesElements = mouSignatoryField.mouSignatories?.map((mouUser, index) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <p key={index}>
          {mouUser.firstName} {mouUser.lastName} ({mouUser.emailAddress})
        </p>
      );
    });

    return (
      <FieldGroup.Field className="tablet:grid-col-6" name="Signatory">
        {Array.isArray(mouSignatoriesElements) && mouSignatoriesElements.length > 0 ? mouSignatoriesElements : '---'}
      </FieldGroup.Field>
    );
  };

  const displayAdmins = (): JSX.Element => {
    if (!adminField) {
      return <></>;
    }

    const adminElements = adminField.admins?.map((admin, index) => {
      // return only email address for MFA admins
      if (isMfaEnvironment && !admin.firstName && !admin.lastName) {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <p key={index}>{admin.emailAddress}</p>
        );
      }

      return (
        // eslint-disable-next-line react/no-array-index-key
        <p key={index}>
          {admin.firstName} {admin.lastName} ({admin.emailAddress})
        </p>
      );
    });

    return (
      <FieldGroup.Field className="tablet:grid-col-6" name={adminField.fieldText}>
        {Array.isArray(adminElements) && adminElements.length > 0 ? adminElements : '---'}
      </FieldGroup.Field>
    );
  };

  return (
    <Section title={sectionText} data-testid="everify-users">
      {displayCorporateAdminMFAAlert && (
        <Alert className="margin-top-4 margin-bottom-neg-1" status="info" slim>
          <Alert.Text className="grid-col-9">
            This email address is associated with an existing E-Verify user. Name and phone number are stored in a
            shared user profile and will be visible after the user signs in for the first time.
          </Alert.Text>
        </Alert>
      )}
      <FieldGroup className="grid-row grid-gap">
        {showPointOfContact ? <PointOfContact /> : <></>}
        {flipOrder ? (
          <>
            {displayAdmins()}
            {displayMOUSignatory()}
          </>
        ) : (
          <>
            {displayMOUSignatory()}
            {displayAdmins()}
          </>
        )}
      </FieldGroup>
      <div
        className={classNames({
          'tablet:grid-offset-6 tablet:padding-left-1': rightAlignOnEditButton,
          'tablet:grid-col-12': !rightAlignOnEditButton,
        })}
      >
        <Button variant="secondary" type="button" onClick={onEditButtonClick}>
          {editButtonText}
        </Button>
      </div>
    </Section>
  );
};

export default BaseEVerifyUsersReview;
